import { firebaseAuth } from './firebase';

export async function getAuthToken(): Promise<string> {
  const token = await firebaseAuth.currentUser?.getIdToken();
  if (!token) throw new Error('Not authenticated');
  return token;
}

/** Error thrown by apiFetch for non-2xx responses. Carries the HTTP status code. */
export class ApiError extends Error {
  readonly statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

// Delays (ms) between successive retry attempts for transient errors.
// Covers the ~3–5 s window during which `tsx watch` restarts the API server.
const RETRY_DELAYS_MS = [500, 1000, 2000, 3000];

/**
 * Low-level fetch wrapper that transparently retries on 5xx responses and
 * network errors (e.g. connection refused while the dev API server restarts).
 *
 * Returns the Response unchanged for any status < 500 (caller handles 4xx).
 * Throws after all retries are exhausted.
 *
 * Export this for code that manages its own auth headers (e.g. chatApiService)
 * so those callers get the same transient-error resilience as apiFetch.
 */
export async function fetchWithRetry(url: string, init?: RequestInit): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1]));
    }
    try {
      const res = await fetch(url, init);
      if (res.status < 500) return res;
      lastError = new Error(`Request failed: ${res.status}`);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
}

/**
 * Authenticated fetch helper. Sets the Authorization header automatically.
 * Sets Content-Type: application/json for non-FormData bodies.
 * Throws an ApiError with the HTTP status code on non-2xx responses.
 *
 * 5xx responses and network errors are automatically retried up to 4 times
 * with exponential backoff before throwing. The auth token is re-acquired on
 * each attempt. 4xx responses are never retried.
 */
export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1]));
    }

    try {
      // Re-acquire the token on every attempt in case the first was near-expiry.
      const idToken = await getAuthToken();
      const headers = new Headers(options?.headers);
      headers.set('Authorization', `Bearer ${idToken}`);
      if (options?.body !== undefined && !(options.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
      }

      const response = await fetch(path, { ...options, headers });

      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          error?: string | { code?: string; message?: string };
          message?: string;
          errors?: unknown;
        };
        const base =
          (typeof body.error === 'object' ? body.error?.message : body.error) ??
          body.message ??
          `Request failed: ${response.status}`;
        // Field-level validation responses (e.g. 422 FIELD_VALIDATION_FAILED) carry the human-readable
        // reasons in `errors` while `error` is just a machine code - surface those so the UI shows why.
        const reasons = Array.isArray(body.errors)
          ? body.errors.filter((e): e is string => typeof e === 'string' && e.trim().length > 0)
          : [];
        const errorMsg = reasons.length > 0 ? reasons.join('; ') : base;
        const error = new ApiError(errorMsg, response.status);
        // 4xx = permanent client/auth error - throw immediately, do not retry.
        if (response.status < 500) throw error;
        // 5xx = transient server error - record and retry.
        lastError = error;
        continue;
      }

      if (response.status === 204) return undefined as T;
      return response.json() as Promise<T>;
    } catch (err) {
      if (err instanceof ApiError) throw err;
      lastError = err;
    }
  }

  throw lastError;
}
